/* ============================================================
   Token store — localStorage persistence for auth session.
   Plain module (no React) so the API client can read/update
   tokens without circular imports.
   ============================================================ */

import type { AuthResponse } from '../types/auth';

const STORAGE_KEY = 'pm.auth';

type Listener = (auth: AuthResponse | null) => void;
const listeners = new Set<Listener>();

function emit(auth: AuthResponse | null) {
  listeners.forEach((l) => l(auth));
}

/** Subscribe to session changes (save/clear). Returns unsubscribe fn. */
export function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Read the stored session, tolerating corrupted/legacy payloads. */
export function loadAuth(): AuthResponse | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthResponse;
    if (!parsed?.accessToken || !parsed?.refreshToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persist the session and notify subscribers. */
export function saveAuth(auth: AuthResponse): AuthResponse {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  emit(auth);
  return auth;
}

/** Drop the session and notify subscribers. */
export function clearAuth(): void {
  localStorage.removeItem(STORAGE_KEY);
  emit(null);
}

export function getAccessToken(): string | null {
  return loadAuth()?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return loadAuth()?.refreshToken ?? null;
}
